import React from "react";
import { DotLegalEmptyState, DotLegalLink, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import DotLegalTableWithFilterBar, { TableFilterBarProps } from "@common/components/dotLegalTableWithFilterBar/DotLegalTableWithFilterBar";
import { ContractRowTableModel, ContractTableRow } from "./ContractTable.types";
import { useTranslation } from "../../localization/useTranslation";
import { Box, TableCell, TableRow } from "@mui/material";
import EmptyStateSvg from "../contractsEmptyState.svg?react";
import { useUrlProvider } from "../../useUrlProvider";
import { GetContractsPlanLimit } from "../../auth/planProvider/PlanProvider.hooks";
import ContractStatusChip from "../contractOverview/ContractStatusChip.tsx";
import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";

export interface IContractTableProps {
    contracts: Array<ContractTableRow>;
    isLoading: boolean;
    hasData: boolean;
    emptyStateText: string;
    filterBar?: TableFilterBarProps;
}

function ContractTable(props: IContractTableProps) {
    const { translateString, translateDate, translateContractStatusEnum } = useTranslation();
    const { getContractOverviewUrl, getGroupEntityUrl } = useUrlProvider();
    const { contractsPlanLimitData } = GetContractsPlanLimit();

    function getTableHeaders() {
        const headers = Array<ITableHeader<ContractTableRow>>();
        headers.push({ property: "userFriendlyId", text: "Id", align: "left", showOnMobile: true });
        headers.push({ property: "name", text: translateString("contract"), align: "left", showOnMobile: true });
        headers.push({ property: "contractType", text: translateString("contractType"), align: "left", showOnMobile: true });
        headers.push({ property: "businessAreas", text: translateString("businessArea"), align: "left", showOnMobile: true });
        headers.push({ property: "contractManager", text: translateString("contractManager"), align: "left", showOnMobile: true });
        headers.push({ property: "internalContractParties", text: translateString("internalContractParties"), align: "left", showOnMobile: true });
        headers.push({ property: "endDate", text: translateString("endDate"), align: "left", showOnMobile: true });
        headers.push({ property: "status", text: translateString("status"), align: "left", showOnMobile: true });
        return headers;
    }

    return props.isLoading || props.hasData ? (
        <DotLegalTableWithFilterBar
            headers={getTableHeaders()}
            rowsPerPage={30}
            defaultOrderBy={"userFriendlyId"}
            clickableRows={false}
            defaultOrder={"asc"}
            isLoading={props.isLoading}
            data={formatDataToTable(props.contracts)}
            emptyText={translateString("noContractsMatchesQuery")}
            filterBar={props.filterBar}
            renderRow={(row) => (
                <TableRow key={row.id}>
                    <TableCell>{row.userFriendlyId}</TableCell>
                    <TableCell>
                        {contractsPlanLimitData?.limitExceeded ? (
                            row.name
                        ) : (
                            <DotLegalLink bold linkColor="primary" to={getContractOverviewUrl(row.id)}>
                                {row.name}
                            </DotLegalLink>
                        )}
                    </TableCell>
                    <TableCell>{row.contractType ?? ""}</TableCell>
                    <TableCell>
                        <ColoredChipsCell items={row.businessAreas} />
                    </TableCell>
                    <TableCell>{row.contractManager}</TableCell>
                    <TableCell>
                        {row.internalContractParties.map((party, i) => (
                            <React.Fragment key={party.id}>
                                <DotLegalLink linkColor="primary" bold stopPropagation to={getGroupEntityUrl(party.id)}>
                                    {party.name}
                                </DotLegalLink>
                                {i !== row.internalContractParties.length - 1 && <span style={{ marginRight: "4px" }}>,</span>}
                            </React.Fragment>
                        ))}
                    </TableCell>
                    <TableCell>{row.endDate ? translateDate(row.endDate) : ""}</TableCell>
                    <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            <ContractStatusChip value={row.statusString} status={row.status} />
                            {row.isOverdue && <ContractStatusChip value={translateString("expired")} status={"Expired"} />}
                        </Box>
                    </TableCell>
                </TableRow>
            )}
        />
    ) : (
        <DotLegalEmptyState icon={<EmptyStateSvg />} text={props.emptyStateText} />
    );

    function formatDataToTable(data: Array<ContractTableRow>): Array<ContractRowTableModel> {
        return data.map((x) => {
            return {
                ...x,
                businessAreaString: x.businessAreas?.map((x) => x.name).join(", "),
                internalContractPartiesString: x.internalContractParties?.map((x) => x.name).join(", "),
                statusString: translateContractStatusEnum(x.status.toString()),
                endDateString: x.endDate ? translateDate(x.endDate) : "",
            };
        });
    }
}

export default ContractTable;
